<template>
  <v-main style="max-width: 1024px !important; margin: auto; padding-bottom: 100px">
    <v-container :class="isMobile ? 'mb-8' : 'mb-0'">
      <!-- Header - My Message Center -->
      <v-row class="text-white mt-4 mb-4">
        <v-col
          class="d-flex flex-wrap"
          :class="$vuetify.display.xs ? 'align-start pr-0' : 'align-center'"
        >
          <v-icon color="#FFFFFF" :icon="mdiBell" alt="Bell Icon" size="x-large" />
          <h1 class="text-white pl-2 text-wrap flex-grow-1">
            {{ $t('L_my_message_center') }}
          </h1>
        </v-col>
      </v-row>
      <!-- Main Page Content -->
      <v-card :class="isMobile ? 'mb-16' : 'mb-0'">
        <v-card-text>
          <!-- My Notification... -->
          <v-row>
            <v-col cols="12" class="myNotifications">
              <h1 class="text-h1">{{ $t('L_my_notifications') }}</h1>
            </v-col>
          </v-row>
          <!-- All of your available notifications are listed... -->
          <v-row class="bgAllOfYourAvailable">
            <v-col cols="12" class="px-4">
              {{ $t('L_all_of_your_available') }}
            </v-col>
          </v-row>
          <v-row v-if="loading">
            <v-col cols="12">
              <LoadingIndicator :loading="!alertsAndNotificationsLoaded" />
            </v-col>
          </v-row>
          <!-- Notifications -->
          <v-row
            v-for="(item, index) in alertsAndNotifications"
            :key="index + 'alertAndNotify'"
            :class="Number(index) % 2 == 0 ? 'backgroundColorBlue' : 'backgroundColorWhite'"
          >
            <v-col cols="1" sm="1" md="1" lg="1" class="text-center">
              <v-icon
                v-if="item.alertIcon && item.alertIcon.toLowerCase() === 'warning'"
                color="orange"
                :icon="mdiAlert"
                size="large"
              />
              <v-icon
                v-else-if="item.alertIcon && item.alertIcon.toLowerCase() === 'info'"
                color="blue"
                :icon="mdiInformationOutline"
                size="large"
              />
              <v-icon
                v-else-if="item.alertIcon && item.alertIcon.toLowerCase() === 'error'"
                color="red"
                :icon="mdiAlertCircleOutline"
                size="large"
              />
            </v-col>
            <v-col cols="11" sm="11" md="11" lg="11" class="d-flex justify-space-evenly">
              <div>
                <span class="pr-2"
                  >{{
                    $t(
                      item.messages.filter((message: any) => message.language === getLocale)[0]
                        .message
                    )
                  }}
                </span>
                <OnexButton
                  @click="viewNotification(item)"
                  variant="text"
                  color="anchor"
                  class="text-h5 pl-0"
                  >{{
                    item.messages.filter((message: any) => message.language === getLocale)[0]
                      .actionText
                  }}</OnexButton
                >
              </div>
              <v-spacer></v-spacer>
              <div>
                <span v-show="item.allowDismiss">
                  <a class="pl-4" href="javascript:void(0)" @click="dismissAction(item.alertId)">
                    <v-icon size="small" color="#333333" :icon="mdiClose" />
                  </a>
                </span>
                <span v-show="!item.allowDismiss" class="pl-2 paddingForNoDismiss"> &nbsp; </span>
              </div>
            </v-col>
          </v-row>
          <!-- You have no available... -->
          <v-row v-if="!alertsAndNotifications || alertsAndNotifications.length === 0">
            <v-col cols="12" class="py-10">
              <v-img
                id="notification-bell-img"
                height="180"
                width="180"
                src="@/assets/notificationBell.png"
                alt="notification bell"
                class="d-flex mx-auto"
              />
            </v-col>
            <v-col cols="12" id="you-have-no-available" class="font-weight-bold">
              <h4>{{ $t('L_you_have_no_available_notifications') }}</h4>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <NotificationDetail
        v-if="showNotificationDetailDialog"
        :show-notification-detail="showNotificationDetailDialog"
        :notification-id="notificationDetailId"
        @accept-action-clicked="acceptActionClickedOnNotificationDetail"
        @reject-action-clicked="rejectActionClickedOnNotificationDetail"
        @dismiss-action-clicked="dismissActionClickedOnNotificationDetail"
      />
      <VerifyPhoneWrapper
        :allow-edit="true"
        :show="showVerifyPhoneWrapper"
        @show-successful-verify="handleCallsGetAlerts"
        @close-verify-phone-dialog="closeVerifyPhoneDialog"
        @gotoupdatecontact2="goToUpdateContact"
        ref="verifyacc"
      />
      <SuccessVerifyPhone
        v-if="showSuccessfulVerify"
        @close-success-verify-phone="closeSuccessVerifyPhone"
      />
    </v-container>
  </v-main>
</template>

<script lang="ts">
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import NotificationDetail from '@/components/MyNotifications/NotificationDetail.vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { mdiAlert, mdiAlertCircleOutline, mdiBell, mdiClose, mdiInformationOutline } from '@mdi/js';
import { mapActions, mapState, mapStores } from 'pinia';
import { defineComponent, type ComponentOptions } from 'vue';
import VerifyPhoneWrapper from '@/components/VerifyPhoneWrapper/VerifyPhoneWrapper.vue';
import SuccessVerifyPhone from '../home/myaccount/components/MyAccountSuccessVerifyPhone.vue';
import { OnexButton } from '@primerica/onex-ui-lib';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';

const componentOptions: ComponentOptions = {
  name: 'AlertAndNotification',
  components: {
    LoadingIndicator,
    NotificationDetail,
    VerifyPhoneWrapper,
    SuccessVerifyPhone,
    OnexButton,
  },
  data() {
    return {
      alertsAndNotificationsLoaded: true,
      loading: true,
      showNotificationDetailDialog: false,
      notificationDetailId: undefined,
      mdiBell,
      mdiClose,
      mdiAlert,
      mdiInformationOutline,
      mdiAlertCircleOutline,
      showSuccessfulVerify: false,
      showSuccessVerifyPhone: false,
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, [
      'getLocale',
      'alertsAndNotifications',
      'goPaperlessType',
      'verifyPhonePageToLand',
      'showVerifyPhoneWrapper',
    ]),

    isMobile() {
      return CapacitorFacade.isNativePlatform();
    },
  },
  async created() {
    if (this.alertsAndNotifications.length <= 0) {
      await this.getAlerts();
      this.loading = false;
    }
  },
  methods: {
    ...mapActions(useGlobalStore, ['getAlerts', 'dismissAlert']),
    handleCallsGetAlerts() {
      this.globalStore.showVerifyPhoneWrapper = false;
      this.getAlerts();
      this.showSuccessfulVerify = true;
    },
    closeVerifyPhoneDialog() {
      this.globalStore.showVerifyPhoneWrapper = false;
      this.getAlerts();
      this.showSuccessfulVerify = false;
    },
    goToUpdateContact() {
      this.globalStore.showVerifyPhoneWrapper = false;
      this.$refs.updateContactRef.showUpdatePhoneDialog = true;
    },
    closeSuccessVerifyPhone() {
      this.showSuccessfulVerify = false;
    },
    viewNotification(alertObj: { actionType: string; alertId: string } | null) {
      if (alertObj) {
        if (alertObj.actionType === 'PHONE_VERIFICATION') {
          this.globalStore.showVerifyPhoneWrapper = true;
          this.globalStore.verifyPhonePageToLand = 'first';
        } else if (alertObj.actionType === 'PAPERLESS_COMM') {
          this.globalStore.showGoPaperlessWrapper = true;
          this.globalStore.goPaperlessType = 'PAPERLESS_COMM';
        } else if (alertObj.actionType === 'PAPERLESS_COMM_INVESTMENT') {
          this.globalStore.showGoPaperlessWrapper = true;
          this.globalStore.goPaperlessType = 'PAPERLESS_COMM_INVESTMENT';
        } else if (alertObj.actionType === 'ACKNOWLEDGEMENT') {
          this.showNotificationDetail(alertObj.alertId);
        } else if (alertObj.actionType === 'PRIVACY_POLICY') {
          // TODO what should we do
        } else {
          // TODO what should be default action
        }
      }
    },
    showNotificationDetail(alertId: string) {
      this.showNotificationDetailDialog = true;
      this.notificationDetailId = alertId;
    },
    hideNotificationDetailDialog() {
      this.showNotificationDetailDialog = false;
      this.notificationDetailId = undefined;
    },
    async acceptActionClickedOnNotificationDetail() {
      // await this.completeAction(this.notificationDetailId, true);
      this.hideNotificationDetailDialog();
      await this.getAlerts();
    },
    async rejectActionClickedOnNotificationDetail() {
      // await this.completeAction(this.notificationDetailId, false);
      this.hideNotificationDetailDialog();
      await this.getAlerts();
    },
    async dismissActionClickedOnNotificationDetail() {
      // await this.dismissAction(this.notificationDetailId);
      this.hideNotificationDetailDialog();
      await this.getAlerts();
    },
    async dismissAction(alertId: string) {
      await this.dismissAlert(alertId);
      await this.getAlerts();
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.link {
  font-weight: 600;
  color: #0c6dca;
  cursor: pointer;
}
.myNotifications {
  padding-left: 16px;
  padding-right: 16px;
}
.bgAllOfYourAvailable {
  background: #f9f9f9;
}
.contactYourRepButtonWidth {
  width: 264px;
  max-width: 265px;
  min-width: 264px;
}
.picture .agentImage .icon {
  width: 55px;
  height: 55px;
  margin: 0 auto;
  border-radius: 50%;
}
.rep {
  padding: 15px 0;
  background: #eee;
  .button-wrapper .v-btn {
    width: 85%;
    margin: 0 auto;
    background-color: #063562 !important;
  }
}
.backgroundColorBlue {
  background-color: #f5f7fa;
}
.backgroundColorWhite {
  background-color: #ffffff;
}
.borderLeftWall {
  border-left: 1px solid #ccc;
}
.paddingForNoDismiss {
  padding-right: 32px;
}
#you-have-no-available {
  line-height: 1.5;
  text-align: center;
  color: #333;
}
#notification-bell-img {
  margin-top: 18px;
  margin-bottom: 18px;
}
</style>
